import { createI18n } from 'vue-i18n'
import { nextTick } from 'vue'
import type { I18n, Composer } from 'vue-i18n'
import en from '@/locales/en'
import pl from '@/locales/pl'

/**
 * Supported locales
 */
export const SUPPORTED_LOCALES = ['en', 'pl'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/**
 * Default locale
 */
export const DEFAULT_LOCALE: SupportedLocale = 'en'

/**
 * Storage key for persisted locale
 */
export const LOCALE_STORAGE_KEY = 'app-locale'

/**
 * Get initial locale from storage or browser preference
 */
function getInitialLocale(): SupportedLocale {
  // Check localStorage first
  const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (storedLocale && SUPPORTED_LOCALES.includes(storedLocale as SupportedLocale)) {
    return storedLocale as SupportedLocale
  }

  // Check browser language preference
  const browserLocale = navigator.language.split('-')[0]
  if (browserLocale && SUPPORTED_LOCALES.includes(browserLocale as SupportedLocale)) {
    return browserLocale as SupportedLocale
  }

  return DEFAULT_LOCALE
}

/**
 * Create the i18n instance
 */
export const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: getInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: {
    en,
    pl, // Pre-load both locales for immediate availability
  },
  // Enable HTML in messages (use with caution)
  warnHtmlMessage: false,
  // Missing key handler
  missing: (locale, key) => {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] Missing translation: ${key} (locale: ${locale})`)
    }
    return key
  },
})

/**
 * Dynamically load locale messages
 * Note: Currently both 'en' and 'pl' are pre-loaded, so this function
 * will typically just set the locale. Dynamic loading is preserved for
 * future locale additions.
 */
export async function loadLocaleMessages(locale: SupportedLocale): Promise<void> {
  const i18nGlobal = i18n.global as Composer

  // Get available locales (it's a computed ref in Composition API mode)
  const availableLocales = Array.isArray(i18nGlobal.availableLocales)
    ? i18nGlobal.availableLocales
    : (i18nGlobal.availableLocales as unknown as { value: string[] }).value || []

  // If already loaded, just set the locale
  if (availableLocales.includes(locale)) {
    i18nGlobal.locale.value = locale
    return nextTick()
  }

  // Dynamically import locale messages
  try {
    const messages = await import(`../locales/${locale}/index.ts`)
    i18nGlobal.setLocaleMessage(locale, messages.default)
    i18nGlobal.locale.value = locale
  } catch (error) {
    console.error(`[i18n] Failed to load locale: ${locale}`, error)
    // Fall back to default locale
    i18nGlobal.locale.value = DEFAULT_LOCALE
  }

  return nextTick()
}

/**
 * Set locale and persist to storage
 */
export async function setLocale(locale: SupportedLocale): Promise<void> {
  await loadLocaleMessages(locale)
  localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  document.documentElement.setAttribute('lang', locale)
}

/**
 * Get current locale
 */
export function getCurrentLocale(): SupportedLocale {
  const i18nGlobal = i18n.global as Composer
  return i18nGlobal.locale.value as SupportedLocale
}

/**
 * Check if a locale is supported
 */
export function isLocaleSupported(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale)
}

/**
 * Get display name for a locale
 */
export function getLocaleDisplayName(locale: SupportedLocale): string {
  const names: Record<SupportedLocale, string> = {
    en: 'English',
    pl: 'Polski',
  }
  return names[locale] || locale
}

/**
 * Type-safe translation helper
 * Provides type inference for translation keys
 */
export type MessageSchema = typeof en

export type { I18n, Composer }
